import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Clock, Briefcase, ExternalLink, Loader2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PublicLayout } from '@/layouts/PublicLayout';
import { careerPageApi, type CareerPageResponse } from '@/lib/career-page-api';

/** Format a number as Brazilian Real currency */
function formatSalary(value: number): string {
  return `R$ ${value.toLocaleString('pt-BR')}`;
}

/** Return relative time string in pt-BR (e.g. "há 3 dias") */
function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 1) return 'hoje';
  if (diffDays === 1) return 'há 1 dia';
  if (diffDays < 30) return `há ${diffDays} dias`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths === 1) return 'há 1 mês';
  return `há ${diffMonths} meses`;
}

export default function CareerListPage() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const [data, setData] = useState<CareerPageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!orgSlug) return;
    setLoading(true);
    careerPageApi
      .getCareerPage(orgSlug)
      .then((res) => {
        setData(res);
        document.title = `Vagas em ${res.organization.name} | TalentumIQ`;
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [orgSlug]);

  if (loading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </PublicLayout>
    );
  }

  if (error || !data) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-muted-foreground text-lg">
            Esta página de carreiras não está disponível.
          </p>
        </div>
      </PublicLayout>
    );
  }

  const { organization, jobs } = data;
  const { careerPage } = organization;

  return (
    <PublicLayout primaryColor={careerPage.primaryColor}>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Org Header */}
        <div className="text-center mb-10">
          {organization.logoUrl && (
            <img
              src={organization.logoUrl}
              alt={organization.name}
              className="h-16 w-auto mx-auto mb-4 object-contain"
            />
          )}
          <h1 className="text-3xl font-bold mb-2">{organization.name}</h1>
          {careerPage.title && (
            <h2 className="text-xl text-muted-foreground mb-2">
              {careerPage.title}
            </h2>
          )}
          {careerPage.description && (
            <p className="text-muted-foreground max-w-2xl mx-auto mb-3">
              {careerPage.description}
            </p>
          )}
          {organization.websiteUrl && (
            <a
              href={organization.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
              Visitar site <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>

        {/* Job Cards */}
        {jobs.length === 0 ? (
          <div className="text-center py-16">
            <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground text-lg">
              Nenhuma vaga aberta no momento. Volte em breve!
            </p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {jobs.map((job) => (
              <Link
                key={job.jobId}
                to={`/careers/${orgSlug}/${job.jobId}`}
                className="block"
              >
                <Card className="hover:shadow-md transition-shadow h-full">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">{job.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                      {job.location && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" />
                          {job.location}
                        </span>
                      )}
                      {job.contractType && (
                        <Badge variant="secondary">{job.contractType}</Badge>
                      )}
                    </div>
                    {job.salaryRange && (
                      <p className="text-sm font-medium">
                        {formatSalary(job.salaryRange.min)} –{' '}
                        {formatSalary(job.salaryRange.max)}
                      </p>
                    )}
                    {job.openedAt && (
                      <p className="text-xs text-muted-foreground inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {relativeTime(job.openedAt)}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
